"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  Tooltip,
  Grid,
  AppBar,
  Toolbar,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Snackbar,
  Alert,
  TableContainer,
  TablePagination,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  alpha,
  styled,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Work as WorkIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import axios from "axios";
 import { IonContent, IonPage, IonRouterLink } from "@ionic/react";
import { useHistory } from "react-router";
import { requestID, baseUrl, config } from "../../../common/utils";

// Define Job type
type Job = {
  job_id?: string;
  title: string;
  job_type: string;
  date_created: string;
  expiration_date: string;
  status?: string;
  category?: string;
  summary?: string;
  responsibilities?: string;
  requirements?: string;
  qualifications?: string;
  benefits?: string;
  min_salary?: string;
  max_salary?: string;
  company_name?: string;
  company_description?: string;
  website?: string;
  application_email?: string;
  description?: string;
  branch_id?: string;
  business_id?: string;
  batch_id?: string;
};

// Styled components for modern look
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  background: theme.palette.background.paper,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  fontWeight: 500,
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 12,
}));

// JobFormDialog component
interface JobFormDialogProps {
  open: boolean;
  onClose: () => void;
  job?: Job | null;
  onSave: (job: Job) => void;
  loading: boolean;
}

const JobFormDialog: React.FC<JobFormDialogProps> = ({
  open,
  onClose,
  job,
  onSave,
  loading,
}) => {
  const [formValues, setFormValues] = useState<Job>(
    job || {
      title: "",
      job_type: "",
      date_created: "",
      expiration_date: "",
      summary: "",
      responsibilities: "",
      requirements: "",
      qualifications: "",
      benefits: "",
      min_salary: "",
      max_salary: "",
      company_name: "",
      company_description: "",
      website: "",
      application_email: "",
      description: "",
    }
  );

  useEffect(() => {
    setFormValues(
      job || {
        title: "",
        job_type: "",
        date_created: "",
        expiration_date: "",
        summary: "",
        responsibilities: "",
        requirements: "",
        qualifications: "",
        benefits: "",
        min_salary: "",
        max_salary: "",
        company_name: "",
        company_description: "",
        website: "",
        application_email: "",
        description: "",
      }
    );
  }, [job]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSave(formValues);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {job ? "Edit Job" : "Add New Job"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title"
              name="title"
              value={formValues.title}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Type"
              name="job_type"
              value={formValues.job_type}
              onChange={handleChange}
              required
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date Created"
              name="date_created"
              type="date"
              value={formValues.date_created}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Expiration Date"
              name="expiration_date"
              type="date"
              value={formValues.expiration_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Summary"
              name="summary"
              value={formValues.summary}
              onChange={handleChange}
              multiline
              rows={3}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Responsibilities"
              name="responsibilities"
              value={formValues.responsibilities}
              onChange={handleChange}
              multiline
              rows={4}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Requirements"
              name="requirements"
              value={formValues.requirements}
              onChange={handleChange}
              multiline
              rows={4}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Qualifications"
              name="qualifications"
              value={formValues.qualifications}
              onChange={handleChange}
              multiline
              rows={4}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Benefits"
              name="benefits"
              value={formValues.benefits}
              onChange={handleChange}
              multiline
              rows={3}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Min Salary"
              name="min_salary"
              value={formValues.min_salary}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Max Salary"
              name="max_salary"
              value={formValues.max_salary}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Company Name"
              name="company_name"
              value={formValues.company_name}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Website"
              name="website"
              value={formValues.website}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company Description"
              name="company_description"
              value={formValues.company_description}
              onChange={handleChange}
              multiline
              rows={3}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Application Email"
              name="application_email"
              value={formValues.application_email}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Description (Full)"
              name="description"
              value={formValues.description}
              onChange={handleChange}
              multiline
              rows={5}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            loading ||
            !formValues.title ||
            !formValues.job_type ||
            !formValues.expiration_date
          }
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{ borderRadius: 2 }}
        >
          {job ? "Update Job" : "Create Job"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const JobListPage: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Job Details Dialog
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  // Add/Edit Job Dialog
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [jobToEdit, setJobToEdit] = useState<Job | null>(null);

  const [filters, setFilters] = useState({
    title: "",
    expiration_date: "",
    job_type: "",
    date_created: "",
    status: "",
  });

  const [sortConfig, setSortConfig] = useState({
    field: "date_created",
    direction: "desc" as "asc" | "desc",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}jobs/getAllJobs`,
        payload,
        config
      );
      console.log(res);
      setJobs(res.data.data);
    } catch (err) {
      showSnackbar("Failed to fetch jobs.", "error");
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    let result = jobs.filter(
      (job) =>
        (filters.title
          ? job.title.toLowerCase().includes(filters.title.toLowerCase())
          : true) &&
        (filters.date_created
          ? job.date_created.includes(filters.date_created)
          : true) &&
        (filters.expiration_date
          ? job.expiration_date.includes(filters.expiration_date)
          : true) &&
        (filters.job_type
          ? job.job_type.toLowerCase().includes(filters.job_type.toLowerCase())
          : true) &&
        (filters.status
          ? job.status?.toLowerCase().includes(filters.status.toLowerCase())
          : true)
    );

    // Sorting
    result = result.sort((a, b) => {
      const aValue = a[sortConfig.field as keyof Job] || "";
      const bValue = b[sortConfig.field as keyof Job] || "";

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredJobs(result);
    setPage(0); // Reset to first page when filters change
  }, [filters, jobs, sortConfig]);

  const handleFilterChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilters({ ...filters, [field]: e.target.value });
    };

  const handleSort = (field: string) => {
    setSortConfig({
      field,
      direction:
        sortConfig.field === field && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Pagination slice
  const paginatedJobs = filteredJobs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleViewDetails = (job: Job) => {
    setSelectedJob(job);
    setShowDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setShowDetailsDialog(false);
    setSelectedJob(null);
  };

  const handleAddJob = () => {
    setJobToEdit(null);
    setShowFormDialog(true);
  };

  const handleEditJob = (job: Job) => {
    setJobToEdit(job);
    setShowFormDialog(true);
  };

  const handleSaveJob = async (jobData: Job) => {
    setActionLoading("save");
    try {
      const payload = {
        requestID: requestID(),
        ...jobData,
        business_id: "bus_id_b96c58e8adabb47c000dc8dc5c02900f",
        branch_id: "bra_id_b96c58e8adabb47c038dc8dc5c00009m",
      };

      if (jobToEdit) {
        await axios.post(
          `${baseUrl()}/jobs/editJob`,
          { ...payload, job_id: jobToEdit.job_id },
          config
        );
        showSnackbar("Job updated successfully!");
      } else {
       const res =  await axios.post(`${baseUrl()}jobs/addJob`, payload, config);
       console.log({res})
        showSnackbar("Job added successfully!");
      }
      fetchJobs();
      setShowFormDialog(false);
    } catch (err) {
      showSnackbar("Failed to save job.", "error");
      console.error("Save job error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteJob = async (jobId?: string) => {
    if (!jobId) return;
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    setActionLoading(jobId);
    try {
      await axios.post(
        `${baseUrl()}/jobs/deleteJob`,
        { requestID: requestID(), job_id: jobId },
        config
      );
      showSnackbar("Job deleted successfully!");
      fetchJobs();
    } catch (err) {
      showSnackbar("Failed to delete job.", "error");
      console.error("Delete job error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "closed":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
          {/* Header */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              bgcolor: "primary.main",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            }}
          >
            <Toolbar>
              <Avatar
                sx={{
                  mr: 2,
                  bgcolor: "white",
                  color: theme.palette.primary.main,
                  width: 40,
                  height: 40,
                }}
              >
                <WorkIcon />
              </Avatar>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                Job Listings
              </Typography>
              {/* <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={handleAddJob}
                sx={{
                  ml: 2,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                Add New Job
              </Button> */}
            </Toolbar>
          </AppBar>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Fade in timeout={300}>
              <Box>
                {/* Stats Card */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: "white",
                        boxShadow: theme.shadows[4],
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{ fontWeight: 700 }}
                        >
                          {jobs.length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Jobs
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        color: "white",
                        boxShadow: theme.shadows[4],
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{ fontWeight: 700 }}
                        >
                          {jobs.filter((j) => j?.status === "active").length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Active Jobs
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                        color: "white",
                        boxShadow: theme.shadows[4],
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{ fontWeight: 700 }}
                        >
                          {jobs.filter((j) => j.status === "pending").length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Pending Jobs
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                        color: "white",
                        boxShadow: theme.shadows[4],
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{ fontWeight: 700 }}
                        >
                          {
                            jobs.filter(
                              (j) => new Date(j.expiration_date) < new Date()
                            ).length
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Expired Jobs
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <StyledPaper sx={{ p: 3, mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h1"
                      sx={{ fontWeight: 700, color: "primary.main" }}
                    >
                      Manage Job Postings
                    </Typography>
                    <Chip
                      label={`${filteredJobs.length} Jobs`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Search Title"
                        value={filters.title}
                        onChange={handleFilterChange("title")}
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "action.active" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Job Type"
                        value={filters.job_type}
                        onChange={handleFilterChange("job_type")}
                        size="small"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={filters.status}
                          label="Status"
                          onChange={(e) =>
                            setFilters({ ...filters, status: e.target.value })
                          }
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="closed">Closed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Date Created"
                        type="date"
                        value={filters.date_created}
                        onChange={handleFilterChange("date_created")}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Deadline"
                        type="date"
                        value={filters.expiration_date}
                        onChange={handleFilterChange("expiration_date")}
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={() =>
                          setFilters({
                            title: "",
                            expiration_date: "",
                            job_type: "",
                            date_created: "",
                            status: "",
                          })
                        }
                        sx={{ height: "40px" }}
                      >
                        Clear
                      </Button>
                    </Grid>
                  </Grid>

                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                      <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading jobs...
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <TableContainer
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1
                          )}`,
                          overflow: "hidden",
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <StyledTableHeadCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleSort("title")}
                                >
                                  Title
                                  <SortIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                </Box>
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleSort("job_type")}
                                >
                                  Job Type
                                  <SortIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                </Box>
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleSort("date_created")}
                                >
                                  Date Created
                                  <SortIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                </Box>
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleSort("expiration_date")}
                                >
                                  Deadline
                                  <SortIcon sx={{ fontSize: 16, ml: 0.5 }} />
                                </Box>
                              </StyledTableHeadCell>
                              <StyledTableHeadCell>Status</StyledTableHeadCell>
                              <StyledTableHeadCell>Actions</StyledTableHeadCell>
                              <StyledTableHeadCell>
                                Applicants
                              </StyledTableHeadCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedJobs.length > 0 ? (
                              paginatedJobs.map((job, idx) => (
                                <TableRow
                                  key={job.job_id || idx}
                                  hover
                                  sx={{
                                    "&:last-child td": { borderBottom: 0 },
                                  }}
                                >
                                  <StyledTableCell sx={{ fontWeight: 600 }}>
                                    {job.title}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Chip
                                      label={job.job_type}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {new Date(
                                      job.date_created
                                    ).toLocaleDateString()}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Chip
                                      label={new Date(
                                        job.expiration_date
                                      ).toLocaleDateString()}
                                      size="small"
                                      color={
                                        new Date(job.expiration_date) <
                                        new Date()
                                          ? "error"
                                          : "default"
                                      }
                                      variant={
                                        new Date(job.expiration_date) <
                                        new Date()
                                          ? "filled"
                                          : "outlined"
                                      }
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <StatusChip
                                      label={job.status || "Unknown"}
                                      size="small"
                                      color={getStatusColor(job.status)}
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Stack direction="row" spacing={1}>
                                      <Tooltip title="View Details">
                                        <IconButton
                                          onClick={() => handleViewDetails(job)}
                                          color="primary"
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={() => handleEditJob(job)}
                                          color="info"
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.info.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.info.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          onClick={() =>
                                            handleDeleteJob(job.job_id)
                                          }
                                          color="error"
                                          size="small"
                                          disabled={
                                            actionLoading === job.job_id
                                          }
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.error.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.error.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          {actionLoading === job.job_id ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <DeleteIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    <Button
                                      variant="outlined"
                                      onClick={() => {
                                        history.push(
                                          `/folder/applicants/${job.job_id}`
                                        );
                                      }}
                                      size="small"
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontWeight: 500,
                                      }}
                                    >
                                      View Applicants
                                    </Button>
                                  </StyledTableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={7}
                                  align="center"
                                  sx={{ py: 4 }}
                                >
                                  <Box sx={{ textAlign: "center", py: 4 }}>
                                    <WorkIcon
                                      sx={{
                                        fontSize: 64,
                                        color: "text.secondary",
                                        mb: 2,
                                      }}
                                    />
                                    <Typography
                                      variant="h6"
                                      color="text.secondary"
                                      gutterBottom
                                    >
                                      No jobs found
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Try adjusting your filters or add a new
                                      job posting.
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {filteredJobs.length > 0 && (
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          component="div"
                          count={filteredJobs.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          sx={{
                            borderTop: `1px solid ${alpha(
                              theme.palette.divider,
                              0.1
                            )}`,
                            "& .MuiTablePagination-toolbar": {
                              padding: 2,
                            },
                          }}
                        />
                      )}
                    </Box>
                  )}
                </StyledPaper>
              </Box>
            </Fade>

            {/* Job Details Dialog */}
            <Dialog
              open={showDetailsDialog}
              onClose={handleCloseDetailsDialog}
              fullWidth
              maxWidth="md"
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Job Details: {selectedJob?.title}
                  </Typography>
                  <IconButton
                    onClick={handleCloseDetailsDialog}
                    sx={{ color: "white" }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 3 }}>
                {selectedJob && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Job Type:
                      </Typography>
                      <Typography>{selectedJob.job_type}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Status:
                      </Typography>
                      <StatusChip
                        label={selectedJob.status || "N/A"}
                        size="small"
                        color={getStatusColor(selectedJob.status)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Category:
                      </Typography>
                      <Typography>{selectedJob.category || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Date Created:
                      </Typography>
                      <Typography>{selectedJob.date_created}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Expiration Date:
                      </Typography>
                      <Typography>{selectedJob.expiration_date}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Salary Range:
                      </Typography>
                      <Typography>
                        {selectedJob.min_salary} - {selectedJob.max_salary}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Summary:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.summary || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Responsibilities:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.responsibilities || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Requirements:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.requirements || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Qualifications:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.qualifications || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Benefits:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.benefits || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Company Name:
                      </Typography>
                      <Typography>
                        {selectedJob.company_name || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Website:
                      </Typography>
                      <Typography>{selectedJob.website || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Company Description:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.company_description || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Application Email:
                      </Typography>
                      <Typography>
                        {selectedJob.application_email || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Full Description:
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {selectedJob.description || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button
                  onClick={handleCloseDetailsDialog}
                  variant="contained"
                  sx={{ borderRadius: 2 }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* Add/Edit Job Form Dialog */}
            <JobFormDialog
              open={showFormDialog}
              onClose={() => setShowFormDialog(false)}
              job={jobToEdit}
              onSave={handleSaveJob}
              loading={actionLoading === "save"}
            />

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  boxShadow: theme.shadows[3],
                }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default JobListPage;
